import { sInputKeyValidationHelper, sIsValidPostcode } from '../../shared.min.js';

const helpers = {
	removeChildrenHelper: parentElement => {
		while (parentElement.firstChild) {
			parentElement.removeChild(parentElement.firstChild);
		}
	},

	sortCentreHelper: (centres, sortBy = 'distance', order = 'ascending') => {
		//order helper
		if (order === 'ascending') return centres.sort((a, b) => a[sortBy] - b[sortBy]);
		if (order === 'descending') return centres.sort((a, b) => b[sortBy] - a[sortBy]);
	},

	decToPercentHelper: decimal => {
		// Convert a decimal to a percentage string
		if (typeof decimal !== 'number') return 'N/A';
		return `${Math.round(decimal * 100)}%`;
	},

	createElementHelper: (tagType, attributes = {}, textContent = '', parentElement) => {
		const element = document.createElement(tagType);

		Object.entries(attributes).forEach(([key, value]) => {
			element.setAttribute(key, value);
		});

		if (textContent) element.textContent = textContent;
		if (parentElement) parentElement.appendChild(element);

		return element;
	},

	toggleNodeVisibility: (node, isVisible) => {
		if (!node) return;

		if (isVisible) {
			node.classList.remove('display-none');
		} else {
			node.classList.add('display-none');
		}
	},

	displayFieldErrorHelper: (errorMessage, input) => {
		const field = input.closest('.field-group');
		const errorElement = field.querySelector('.error-message');
		if (!errorElement || !field) return;

		errorElement.textContent = errorMessage;

		field.classList.add('field-group--error');
	},

	createDirectionsLinkHelper: (postcode, destination) => {
		// Create a Google Maps directions link
		const baseUrl = 'https://www.google.com/maps/dir/?api=1';
		const encodedOrigin = `&origin=${encodeURIComponent(postcode)}`; // User's postcode
		const encodedDestination = `&destination=${encodeURIComponent(destination)}`; // Placeholder destination

		return `${baseUrl}${encodedOrigin}${encodedDestination}`;
	},
};

const drivingTests = {
	resultsSection: null,
	resultsTable: null,
	resultsPostcode: null,
	form: null,
	toggleLabel: null,
	drivingCentreData: null,

	init: async function () {
		const {
			postcodeInput,
			tableToggle,
			submitButton,
			resultsSection,
			editPostcodeTop,
			editPostcodeBottom,
			userPostcode,
			resultsTable,
			form,
			toggleLabel,
			dataNode,
		} = this.getWidgetNodes();

		if (postcodeInput && typeof sInputKeyValidationHelper !== 'undefined')
			sInputKeyValidationHelper(
				postcodeInput,
				'alphaNumeric',
				postcodeInput.closest('.field-group'),
				'field-group--error'
			);

		// set widget nodes
		this.resultsSection = resultsSection;
		this.resultsTable = resultsTable;
		this.resultsPostcode = userPostcode;
		this.form = form;
		this.toggleLabel = toggleLabel;

		const drivingCentreData = await this.getDrivingCentreData(dataNode);

		if (!drivingCentreData) {
			helpers.displayFieldErrorHelper(postcodeInput.getAttribute('data-error-noData'), postcodeInput);
			return;
		}

		//set event listeners
		if (submitButton) submitButton.addEventListener('click', e => this.handleSubmitPostcode(e, postcodeInput));
		if (editPostcodeTop) editPostcodeTop.addEventListener('click', e => this.handleEditPostcode(e, tableToggle));
		if (editPostcodeBottom)
			editPostcodeBottom.addEventListener('click', e => this.handleEditPostcode(e, tableToggle));
		if (tableToggle) tableToggle.addEventListener('click', e => this.handleTableToggleSwitched(e));
		if (form) {
			form.addEventListener('submit', e => this.handleSubmitPostcode(e, postcodeInput));
		}
	},

	getDrivingCentreData: async function (dataNode) {
		const jsonUrl = dataNode.getAttribute('data-driving-centre-data');
		if (!jsonUrl) return null;

		try {
			const response = await fetch(jsonUrl);
			if (!response.ok) return null;

			const data = await response.json();
			this.drivingCentreData = data;
			return data;
		} catch (err) {
			console.error('Error fetching:', err);
			return null;
		}
	},

	getWidgetNodes: () => {
		return {
			postcodeInput: document.getElementById('dtpostcode'),
			tableToggle: document.getElementById('dt-toggle-table-order'),
			resultsSection: document.getElementById('dt-results'),
			editPostcodeTop: document.getElementById('dt-edit-postcode'),
			editPostcodeBottom: document.getElementById('dt-edit-postcode-bottom'),
			userPostcode: document.getElementById('dt-user-postcode'),
			resultsTable: document.getElementById('dt-results-table'),
			form: document.getElementById('dt-postcode-form'),
			toggleLabel: document.getElementById('dt-toggle-table-label'),
			dataNode: document.getElementById('dt-data'),
			submitButton: document.getElementById('dt-submit-postcode'),
		};
	},

	handleEditPostcode: function (e, tableToggle) {
		e.preventDefault();

		helpers.removeChildrenHelper(this.resultsTable);
		helpers.toggleNodeVisibility(this.form, true);
		helpers.toggleNodeVisibility(this.resultsSection, false);
		tableToggle.checked = false; // Reset the toggle to default state
	},

	handleSubmitPostcode: async function (e, postcodeInput) {
		e.preventDefault();

		const isValid = this.validatePostcode(postcodeInput);

		if (!isValid) return;

		const validTestCentres = await this.findNearestTestCentres(postcodeInput.value);

		if (!validTestCentres) {
			// postcode not found
			helpers.displayFieldErrorHelper(postcodeInput.getAttribute('data-error-noneFound'), postcodeInput);
			return;
		}

		this.resultsPostcode.innerText = `${postcodeInput.value.toUpperCase()} postcode`;
		helpers.toggleNodeVisibility(this.form, false);
		helpers.toggleNodeVisibility(this.resultsSection, true);
	},

	validatePostcode: postcodeInput => {
		const postcode = postcodeInput.value.trim();

		const errorMessages = {
			empty: postcodeInput.dataset.errorEmpty,
			tooShort: postcodeInput.dataset.errorTooshort,
			tooLong: postcodeInput.dataset.errorToolong,
			invalidFormat: postcodeInput.dataset.errorInvalidformat,
		};

		const { isValid, errorMessage } =
			typeof sInputKeyValidationHelper !== 'undefined'
				? sIsValidPostcode(postcode, errorMessages)
				: { isValid: true, errorMessage: '' };

		if (!isValid) helpers.displayFieldErrorHelper(errorMessage, postcodeInput);

		return isValid;
	},

	convertUserPostcode: postcode => {
		// convert user postcode to lat/lng
		if (!postcode) return Promise.reject(`Postcode is required`);

		const geocoder = new google.maps.Geocoder();

		return new Promise((resolve, reject) => {
			// Geocode the postcode to get latitude and longitude
			geocoder.geocode({ address: postcode }, (results, status) => {
				if (status === 'OK' && results[0]) {
					// Get the coordinates from the geocoding result
					const latitude = results[0].geometry.location.lat();
					const longitude = results[0].geometry.location.lng();

					// Check if the location is in London
					const inLondon = results[0].address_components.some(component => {
						return component.long_name.toLowerCase().includes('london');
					});

					resolve({ latitude, longitude, inLondon });
				} else {
					console.error(`Geocode was not successful due to: ${status}`);
					reject('Postcode not found. Please try again.');
				}
			});
		});
	},

	handleTableToggleSwitched: function (e) {
		const isChecked = e.target.checked;
		const results = Array.from(this.resultsTable.querySelectorAll('.mod-results-grid__result'));

		const plusLink = this.resultsTable.querySelector('.plus-link') || null;

		this.toggleLabel.innerText = isChecked ? 'Distance' : 'Pass rate';

		// Reset results (clear table, or whatever reset functionality you have)
		helpers.removeChildrenHelper(this.resultsTable);

		// Determine the sorting attribute based on the toggle state
		const sortBy = isChecked ? 'data-distance' : 'data-score';
		const sortOrder = isChecked ? 1 : -1; // Ascending for distance, descending for score

		// Sort results by the selected attribute
		const sortedTestCentres = results.sort((a, b) => {
			const valueA = parseFloat(a.getAttribute(sortBy)) || null;
			const valueB = parseFloat(b.getAttribute(sortBy)) || null;
			if (valueA === null || valueB === null) return 0; // If either value is null, do not change order

			return sortOrder * (valueA - valueB);
		});

		// Append sorted results to the results grid
		sortedTestCentres.forEach(result => this.resultsTable.appendChild(result));

		if (plusLink) this.resultsTable.appendChild(plusLink);
	},

	calculateAllDistances: (userLatLng, testCentres) => {
		return testCentres.map(testCentre => {
			const distance = drivingTests.calculateDistance(userLatLng, testCentre);

			return {
				...testCentre,
				distance: distance, // Add the calculated distance to the test centre object
			};
		});
	},

	calculateDistance: (userLatLng, centre) => {
		// Haversine formula for calculating distance between two points on the Earth
		const R = 3958.8; // Radius of the Earth in miles (use 6371 for kilometers)

		const lat1 = userLatLng.latitude; // User's latitude
		const lng1 = userLatLng.longitude; // User's longitude
		const lat2 = centre.lat; // Test centre's latitude
		const lng2 = centre.lng; // Test centre's longitude

		const dLat = (lat2 - lat1) * (Math.PI / 180);
		const dLng = (lng2 - lng1) * (Math.PI / 180);

		const a =
			Math.sin(dLat / 2) * Math.sin(dLat / 2) +
			Math.cos(lat1 * (Math.PI / 180)) *
				Math.cos(lat2 * (Math.PI / 180)) *
				Math.sin(dLng / 2) *
				Math.sin(dLng / 2);

		const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));

		return R * c; // Returns distance in miles
	},

	getNearestTestCentres: async function (testCentres, userLatLng) {
		// Sort the testCentres array by distance
		const sortedTestCentres = helpers.sortCentreHelper(testCentres, 'distance', 'ascending');

		// Limit to the closest 20 test centres for distance matrix request
		const maxUnits = 20;
		const drivingDistance = await this.requestClosestTestCenters(sortedTestCentres.slice(0, maxUnits), userLatLng);

		const closestTestCentersByDrivingDistance = helpers.sortCentreHelper(drivingDistance, 'distance', 'ascending');

		// Return the top 5 nearest test centres, if London, return top 10
		const returnedResults = userLatLng.inLondon ? 10 : 5;
		return closestTestCentersByDrivingDistance.slice(0, returnedResults);
	},

	requestClosestTestCenters: async (testCentres, userLatLng) => {
		const service = new google.maps.DistanceMatrixService();
		const origin = new google.maps.LatLng(userLatLng.latitude, userLatLng.longitude);
		const destinations = testCentres.map(centre => new google.maps.LatLng(centre.lat, centre.lng));

		const response = await new Promise((resolve, reject) => {
			service.getDistanceMatrix(
				{
					origins: [origin],
					destinations,
					travelMode: google.maps.TravelMode.DRIVING,
					unitSystem: google.maps.UnitSystem.IMPERIAL,
				},
				(response, status) => {
					if (status !== 'OK') reject(`Error retrieving distance matrix: ${status}`);
					else resolve(response);
				}
			);
		});

		return testCentres.map((testCentre, index) => {
			const element = response.rows[0].elements[index];
			const distance = element.status === 'OK' ? element.distance.value / 1609.34 : null;

			return {
				...testCentre,
				distance,
			};
		});
	},

	formatClosestTestCentres: (testCentres, userPostcode) => {
		return testCentres.map(testCentre => {
			//Structure the test centre data for display
			return {
				testCentreData: {
					title: {
						title: testCentre.center,
						value: helpers.decToPercentHelper(testCentre.pass_rate),
						innerElement: { tagType: 'span', attributes: {}, textContent: ' pass rate' },
					},
					address: {
						title: 'Location',
						value: testCentre.address,
					},
					distance: {
						title: 'Distance',
						value: `${testCentre.distance.toFixed(2)} miles`,
						innerElement: {
							tagType: 'a',
							attributes: {
								href: helpers.createDirectionsLinkHelper(userPostcode, testCentre.address.Postcode),
								class: 'block',
								target: '_blank',
							},
							textContent: 'Get directions',
						},
					},
					tests: {
						title: 'Types of tests',
						value: testCentre.tests,
					},
					disabled_access: {
						title: 'Accessibility',
						value: testCentre.disabled_access,
					},
				},
				distance: testCentre.distance, // set as data attribute for sorting toggle
				score: testCentre.pass_rate, // set as data attribute for sorting toggle
				isHighlighted: false, // Default to false, will be set later
			};
		});
	},

	setHighlightedTestCentre: testCentres => {
		return testCentres.map((testCentre, index) => {
			if (index === 0) testCentre.isHighlighted = true;
			return testCentre;
		});
	},

	displayNearestTestCentres: function (nearestTestCentres) {
		//loop through test centres and create elements
		nearestTestCentres.forEach((centre, index) => {
			//for each test centre, create a result container
			const result = helpers.createElementHelper('div', {
				class: `mod-results-grid__result ${centre.isHighlighted ? 'mod-results-grid__result--highlighted' : ''} ${index > 4 ? 'display-none' : ''}`,
				'data-distance': centre.distance,
				'data-score': centre.score,
			});

			//checks if the centre is highlighted, if so, create a banner
			if (centre.isHighlighted) {
				const banner = helpers.createElementHelper(
					'div',
					{ class: 'mod-results-grid__result__banner' },
					'',
					result
				);
				helpers.createElementHelper(
					'img',
					{
						src: 'https://www.confused.com/static/content/images/icons/black-header-tick--black.svg',
						alt: 'Driving Tests Illustration',
						class: 'mod-results-grid__result__banner__icon',
					},
					'',
					banner
				);
				helpers.createElementHelper(
					'h3',
					{ class: 'mod-results-grid__result__banner__title' },
					'Best pass rate',
					banner
				);
			}

			// Create the result items container
			const resultItems = helpers.createElementHelper(
				'div',
				{ class: 'mod-results-grid__result__items' },
				'',
				result
			);

			//loop through the test centre data and create elements for each item
			Object.entries(centre.testCentreData).forEach(([key, value]) => {
				const item = helpers.createElementHelper(
					'div',
					{ class: 'mod-results-grid__result__item' },
					'',
					resultItems
				);

				helpers.createElementHelper(
					'span',
					{ class: 'mod-results-grid__result__item__title' },
					value.title,
					item
				);

				if (key === 'address') {
					const itemValue = helpers.createElementHelper(
						'span',
						{ class: 'mod-results-grid__result__item__value' },
						'',
						item
					);
					const addressObject = value;
					const address = helpers.createElementHelper('address', {}, '', itemValue);

					Object.entries(addressObject.value).forEach(([addressKey, addressValue]) => {
						addressValue && helpers.createElementHelper('div', {}, addressValue, address);
					});
				} else {
					const content = helpers.createElementHelper(
						'span',
						{ class: 'mod-results-grid__result__item__value' },
						value.value,
						item
					);
					if (value.innerElement)
						helpers.createElementHelper(
							value.innerElement.tagType,
							value.innerElement.attributes,
							value.innerElement.textContent,
							content
						);
				}
			});

			this.resultsTable.appendChild(result);
		});

		//if london postcode 10 results, otherwise 5, handle show more button
		if (nearestTestCentres.length > 5) {
			const container = helpers.createElementHelper('div', {}, '', this.resultsTable);
			const showMoreButton = helpers.createElementHelper('button', { class: 'plus-link' }, '', container);
			helpers.createElementHelper('span', { class: 'plus-link__title' }, 'Show more', showMoreButton);

			// Add event listener to the button to show more results
			showMoreButton.addEventListener('click', () => {
				const hiddenResults = this.resultsTable.querySelectorAll('.mod-results-grid__result.display-none');
				hiddenResults.forEach(result => {
					result.classList.remove('display-none');
				});
				showMoreButton.remove(); // Remove the button after showing all results
			});
		}
	},

	findNearestTestCentres: async function (postcode) {
		try {
			// Convert the user's postcode to latitude and longitude
			const userLatLng = await this.convertUserPostcode(postcode);
			if (!userLatLng) throw new Error('Unable to convert postcode to coordinates.');

			// get the distances from the user's location to each test centre
			const distances = this.calculateAllDistances(userLatLng, this.drivingCentreData);
			if (!distances || distances.length === 0) throw new Error('Unable to calculate distances to test centres.');

			// find the nearest test centres based on the calculated distances
			const nearestTestCentres = await this.getNearestTestCentres(distances, userLatLng);
			if (!nearestTestCentres || nearestTestCentres.length === 0)
				throw new Error('Unable to find nearest test centres.');

			// sort by pass rate
			const sortedTestCentres = helpers.sortCentreHelper(nearestTestCentres, 'pass_rate', 'descending');
			if (!sortedTestCentres || sortedTestCentres.length === 0)
				throw new Error('Unable to sort test centres by pass rate.');

			// Format the nearest test centres for display
			const formattedTestCentres = this.formatClosestTestCentres(sortedTestCentres, postcode);
			if (!formattedTestCentres || formattedTestCentres.length === 0)
				throw new Error('Unable to format the test centres for display.');

			// Set the highlighted test centre
			const testCentres = this.setHighlightedTestCentre(formattedTestCentres);

			// Display the nearest test centres
			this.displayNearestTestCentres(testCentres);

			return true;
		} catch (error) {
			console.error('Error:', error); // In case of an error, log it

			return false;
		}
	},
};

document.addEventListener('DOMContentLoaded', function () {
	drivingTests.init();
});
